import { useMemo, useState } from 'react';

import impressionSunriseImage from '@/assets/artworks/monet-impression-sunrise.jpg';
import auroraCollectiveImage from '@/assets/users/aurora-collective-avatar.png';
import useGoBack from '@/hooks/useGoBack';
import useTranslations from '@/i18n/useTranslations';
import BackArrowIcon from '@/shared-components/icons/components/public/BackArrowIcon';
import SuarteName from '@/shared-components/icons/components/public/SuarteName';
import StaticSeoHead from '@/shared-components/seo/StaticSeoHead';
import Heading from '@/shared-components/text/components/Heading';
import Text from '@/shared-components/text/components/Text';
import { formatCurrencyAmount } from '@/utils/currency.utils';

import './index.css';

const TRANSACTION_HISTORY = [
  {
    date: '04 Mar 2026',
    owner: 'Mara Cole',
    price: 32400,
  },
  {
    date: '18 Oct 2025',
    owner: 'Private Collector',
    price: 28900,
  },
  {
    date: '12 Jan 2024',
    owner: 'Lumen Gallery',
    price: 24500,
  },
];

const DEMO_PRICE = 32400;
const DEMO_CURRENCY = 'EUR';
const CURRENT_OWNER = {
  name: 'Mara Cole',
  user_image: auroraCollectiveImage,
};

function formatDemoPrice(value) {
  return formatCurrencyAmount(value, DEMO_CURRENCY, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

export default function CertificateOfOwnershipPage() {
  const goBackHandler = useGoBack('/');
  const t = useTranslations('common.certificate_of_ownership');
  const [isBackVisible, setIsBackVisible] = useState(false);
  const [showOwnerHandle, setShowOwnerHandle] = useState(true);

  const ownerHandle = useMemo(
    () => (showOwnerHandle ? '@mara-cole' : t('example.owner_hidden')),
    [showOwnerHandle, t],
  );
  const formattedDemoPrice = formatDemoPrice(DEMO_PRICE);
  const publicPrivateRows = [
    {
      label: t('privacy.rows.always_public.label'),
      items: t('privacy.rows.always_public.items'),
    },
    {
      label: t('privacy.rows.private_owner.label'),
      items: t('privacy.rows.private_owner.items'),
    },
  ];

  return (
    <div className="certificate-page">
      <StaticSeoHead
        title={t('seo.title')}
        description={t('seo.description')}
        canonicalPath="/certificate-of-ownership"
        breadcrumbItems={[
          { name: t('seo.breadcrumb_home'), url: 'https://suarte.art/' },
          { name: t('seo.breadcrumb_current'), url: 'https://suarte.art/certificate-of-ownership' },
        ]}
      />
      <div className="certificate-page__header">
        <BackArrowIcon className="certificate-page__back-button" onClick={goBackHandler} />
        <SuarteName className="certificate-page__brand" />
      </div>
      <nav className="certificate-page__subnav" aria-label={t('navigation.aria_label')}>
        {[
          { href: '#overview', label: t('navigation.overview') },
          { href: '#whats-included', label: t('navigation.information') },
          { href: '#how-it-works', label: t('navigation.how_it_works') },
          { href: '#royalties', label: t('navigation.royalties') },
          { href: '#stay-on-suarte', label: t('navigation.resell') },
          { href: '#security', label: t('navigation.security') },
          { href: '#faqs', label: t('navigation.faqs') },
        ].map((link) => (
          <a key={link.href} href={link.href} className="certificate-page__subnav-link">
            {link.label}
          </a>
        ))}
      </nav>
      <header id="overview" className="certificate-page__hero">
        <div>
          <Heading className="certificate-page__title">
            {t('hero.title')}
          </Heading>
          <Text className="certificate-page__subtitle" paragraph>
            {t('hero.subtitle')}
          </Text>
        </div>
      </header>
      <section id="definition" className="certificate-page__card">
        <Heading className="certificate-page__section-title">
          {t('definition.title')}
        </Heading>
        <div className="certificate-page__definition-copy">
          <Text paragraph className="certificate-page__body-copy">
            {t('definition.body_primary')}
          </Text>
          <Text paragraph className="certificate-page__body-copy">
            {t('definition.body_secondary')}
          </Text>
        </div>
        <div className="certificate-page__badges" role="list">
          {['title', 'updates', 'price', 'privacy'].map((badge) => (
            <span key={badge} role="listitem" className="certificate-page__badge">
              {t(`definition.badges.${badge}`)}
            </span>
          ))}
        </div>
        <aside className="certificate-page__note" role="note">
          {t('definition.note')}
        </aside>
      </section>
      <section className="certificate-page__grid" aria-labelledby="benefits-title">
        <article className="certificate-page__card">
          <Heading
            id="benefits-title"
            className="certificate-page__section-title">
            {t('benefits.title')}
          </Heading>
          <div className="certificate-page__benefit-grid" role="list">
            <div className="benefit-card" role="listitem">
              <div className="benefit-card__head">
                <h3 className="certificate-page__mini-heading">{t('benefits.collectors.title')}</h3>
              </div>
              <ul className="certificate-page__list benefit-card__list">
                <li><strong>{t('benefits.collectors.ownership_title')}</strong> {t('benefits.collectors.ownership_body')}</li>
                <li><strong>{t('benefits.collectors.price_title')}</strong> {t('benefits.collectors.price_body')}</li>
                <li><strong>{t('benefits.collectors.privacy_title')}</strong> {t('benefits.collectors.privacy_body')}</li>
              </ul>
            </div>
            <div className="benefit-card" role="listitem">
              <div className="benefit-card__head">
                <h3 className="certificate-page__mini-heading">{t('benefits.galleries.title')}</h3>
              </div>
              <ul className="certificate-page__list benefit-card__list">
                <li><strong>{t('benefits.galleries.provenance_title')}</strong> {t('benefits.galleries.provenance_body')}</li>
                <li><strong>{t('benefits.galleries.outcomes_title')}</strong> {t('benefits.galleries.outcomes_body')}</li>
                <li><strong>{t('benefits.galleries.issuance_title')}</strong> {t('benefits.galleries.issuance_body')}</li>
              </ul>
            </div>
            <div className="benefit-card" role="listitem">
              <div className="benefit-card__head">
                <h3 className="certificate-page__mini-heading">{t('benefits.market.title')}</h3>
              </div>
              <ul className="certificate-page__list benefit-card__list">
                <li><strong>{t('benefits.market.pricing_title')}</strong> {t('benefits.market.pricing_body')}</li>
                <li><strong>{t('benefits.market.provenance_title')}</strong> {t('benefits.market.provenance_body')}</li>
                <li><strong>{t('benefits.market.incentives_title')}</strong> {t('benefits.market.incentives_body')}</li>
              </ul>
            </div>
          </div>
          <div className="certificate-page__transparency-callout" role="note">
            <span className="transparency-callout__dot" aria-hidden="true" />
            {t('benefits.callout')}
          </div>
        </article>
      </section>
      <section id="whats-included" className="certificate-page__card">
        <Heading className="certificate-page__section-title">
          {t('information.title')}
        </Heading>
        <dl className="certificate-spec" role="list">
          <div className="certificate-spec__row" role="listitem">
            <dt className="certificate-spec__label">{t('information.rows.artwork.label')}</dt>
            <dd className="certificate-spec__content">
              {t('information.rows.artwork.value')}
            </dd>
          </div>
          <div className="certificate-spec__row" role="listitem">
            <dt className="certificate-spec__label">{t('information.rows.transaction.label')}</dt>
            <dd className="certificate-spec__content">
              {t('information.rows.transaction.value')}
            </dd>
          </div>
          <div className="certificate-spec__row" role="listitem">
            <dt className="certificate-spec__label">{t('information.rows.parties.label')}</dt>
            <dd className="certificate-spec__content">
              {t('information.rows.parties.value')} <span className="muted">{t('information.rows.parties.note')}</span>
            </dd>
          </div>
          <div className="certificate-spec__row" role="listitem">
            <dt className="certificate-spec__label">{t('information.rows.identifiers.label')}</dt>
            <dd className="certificate-spec__content">
              {t('information.rows.identifiers.value')} <span className="muted">{t('information.rows.identifiers.note')}</span>
            </dd>
          </div>
          <div className="certificate-spec__row" role="listitem">
            <dt className="certificate-spec__label">{t('information.rows.status.label')}</dt>
            <dd className="certificate-spec__content">
              {t('information.rows.status.active')} <span className="muted">{t('information.rows.status.active_note')}</span> · {t('information.rows.status.inactive')} <span className="muted">{t('information.rows.status.inactive_note')}</span>
            </dd>
          </div>
          <div className="certificate-spec__row" role="listitem">
            <dt className="certificate-spec__label">{t('information.rows.history.label')}</dt>
            <dd className="certificate-spec__content">
              {t('information.rows.history.value')}
            </dd>
          </div>
        </dl>
      </section>
      <section className="certificate-page__card">
        <Heading className="certificate-page__section-title">
          Public vs. private information
        </Heading>
        <div className="certificate-page__mini-table" role="table">
          {publicPrivateRows.map((row) => (
            <div key={row.label} className="certificate-page__mini-table-row" role="row">
              <div className="certificate-page__mini-table-heading" role="rowheader">{row.label}</div>
              <div className="certificate-page__mini-table-cells" role="cell">
                {row.items}
              </div>
            </div>
          ))}
        </div>
      </section>
      <section id="example" className="certificate-page__example">
        <div className="certificate-page__example-controls">
          <Heading className="certificate-page__section-title">
            <span className="certificate-page__example-title--full">{t('example.title')}</span>
            <span className="certificate-page__example-title--compact">{t('example.title_compact')}</span>
          </Heading>
          <div className="certificate-page__example-actions">
            <button
              type="button"
              className="certificate-page__toggle"
              onClick={() => setIsBackVisible((prev) => !prev)}
              aria-pressed={isBackVisible}
            >
              {isBackVisible ? t('example.show_front') : t('example.turn_card')}
            </button>
            <button
              type="button"
              className="certificate-page__toggle"
              onClick={() => setShowOwnerHandle((prev) => !prev)}
              aria-pressed={showOwnerHandle}
            >
              {showOwnerHandle ? t('example.set_private') : t('example.set_public')}
            </button>
          </div>
        </div>

        <div className={`certificate-demo ${isBackVisible ? 'certificate-demo--back' : ''}`}>
          <div className="certificate-demo__card" aria-live="polite">
            <div
              className="certificate-demo__face certificate-demo__face--front"
              aria-hidden={isBackVisible}
            >
              <div className="certificate-demo__artwork">
                <img
                  className="certificate-demo__artwork-image"
                  src={impressionSunriseImage}
                  alt={t('example.artwork_alt')}
                />
                <div className="certificate-demo__artwork-content">
                  <div className="certificate-demo__artwork-meta">
                    <h3>“Impression, Sunrise”</h3>
                    <p>{t('example.artwork_details')}</p>
                  </div>
                  <div className="certificate-demo__price" data-tooltip={t('example.final_price_tooltip')}>
                    {formattedDemoPrice}
                    <span>{t('example.final_price_label')}</span>
                  </div>
                </div>
              </div>
              <div className="certificate-demo__split">
                <div className="certificate-demo__detail">
                  <span className="certificate-demo__label">{t('example.gallery_label')}</span>
                  <span>{t('example.gallery_value')}</span>
                </div>
                <div className="certificate-demo__detail">
                  <span className="certificate-demo__label">{t('example.owner_label')}</span>
                  <span data-tooltip={t('example.owner_tooltip')}>{ownerHandle}</span>
                </div>
              </div>
              <div className="certificate-demo__split">
                <div className="certificate-demo__detail">
                  <span className="certificate-demo__label">{t('example.sale_date_label')}</span>
                  <span>{t('example.sale_date_value')}</span>
                </div>
                <div className="certificate-demo__detail">
                  <span className="certificate-demo__label">{t('example.certificate_id_label')}</span>
                  <span>CO-9824-01</span>
                </div>
              </div>
            </div>
            <div
              className="certificate-demo__face certificate-demo__face--back"
              aria-hidden={!isBackVisible}
            >
              <div className="certificate-demo__current-owner">
                <img
                  src={CURRENT_OWNER.user_image}
                  alt={t('example.owner_profile_alt', { name: CURRENT_OWNER.name })}
                  className="certificate-demo__current-owner-image"
                />
                <div>
                  <span>{t('example.current_owner')}</span>
                  <strong>{CURRENT_OWNER.name}</strong>
                </div>
              </div>
              <div className="certificate-demo__transaction-history">
                <h3>{t('example.transaction_history')}</h3>
                <ul>
                  {TRANSACTION_HISTORY.map((item) => (
                    <li key={`${item.date}-${item.price}`}>
                      <span>{t(`example.history.${item.date}`)}</span>
                      <span>{item.owner === 'Private Collector' ? t('example.private_collector') : item.owner}</span>
                      <strong>{formatDemoPrice(item.price)}</strong>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section id="how-it-works" className="certificate-page__card">
        <Heading className="certificate-page__section-title">
          {t('how_it_works.title')}
        </Heading>
        <ol className="certificate-page__ordered-list">
          <li>
            <strong>{t('how_it_works.steps.sale_recorded.title')}</strong> {t('how_it_works.steps.sale_recorded.body')}
          </li>
          <li>
            <strong>{t('how_it_works.steps.issued.title')}</strong> {t('how_it_works.steps.issued.body')}
          </li>
          <li>
            <strong>{t('how_it_works.steps.privacy.title')}</strong> {t('how_it_works.steps.privacy.body')}
          </li>
          <li>
            <strong>{t('how_it_works.steps.resales.title')}</strong> {t('how_it_works.steps.resales.body')}
          </li>
          <li className="certificate-page__edge-case">
            <strong>{t('how_it_works.steps.refunds.title')}</strong> {t('how_it_works.steps.refunds.body')}
          </li>
        </ol>
      </section>
      <section id="royalties" className="certificate-page__card">
        <Heading className="certificate-page__section-title">
          {t('royalties.title')}
        </Heading>
        <div className="certificate-page__stack">
          <div>
            <h3 className="certificate-page__mini-heading">{t('royalties.eligibility.title')}</h3>
            <p>{t('royalties.eligibility.body')}</p>
          </div>
          <div>
            <h3 className="certificate-page__mini-heading">{t('royalties.distribution.title')}</h3>
            <p>{t('royalties.distribution.body')}</p>
          </div>
          <div>
            <h3 className="certificate-page__mini-heading">{t('royalties.why_it_matters.title')}</h3>
            <p>{t('royalties.why_it_matters.body')}</p>
          </div>
        </div>
      </section>
      <section id="stay-on-suarte" className="certificate-page__card">
        <Heading className="certificate-page__section-title">
          {t('resale.title')}
        </Heading>
        <ul className="certificate-page__list">
          <li>
            <strong className="certificate-page__resale-label">{t('resale.provenance_title')}</strong>{' '}
            {t('resale.provenance_body')}
          </li>
          {['eligibility', 'record', 'ownership', 'protection'].map((item) => (
            <li key={item}>
              <strong className="certificate-page__resale-label">{t(`resale.${item}_title`)}</strong>{' '}
              {t(`resale.${item}_body`)}
            </li>
          ))}
        </ul>
        <div className="certificate-page__alert" role="note">
          <span className="transparency-callout__dot" aria-hidden="true" />
          {t('resale.alert')}
        </div>
      </section>
      <section id="security" className="certificate-page__card certificate-page__card--security">
        <Heading className="certificate-page__section-title">
          {t('security.title')}
        </Heading>
        <ul className="certificate-page__list">
          <li>{t('security.items.verified_galleries')}</li>
          <li>{t('security.items.verification_seal')}</li>
          <li>{t('security.items.verification_url')}</li>
          <li>{t('security.items.downloads')}</li>
        </ul>
        <div className="certificate-page__callout" role="status">
          <span className="certificate-page__lock" aria-hidden="true" />
          <div>
            <strong>{t('security.note.title')}</strong> {t('security.note.body')}
          </div>
        </div>
        <div className="certificate-page__gallery-badge">
          <span className="certificate-page__gallery-chip">
            <span className="transparency-callout__dot" aria-hidden="true" />
            {t('security.gallery_badge')}
          </span>
        </div>
      </section>
      <section id="faqs" className="certificate-page__card">
        <Heading className="certificate-page__section-title">
          {t('faq.title')}
        </Heading>
        <ul className="certificate-page__list certificate-page__list--faq">
          <li>
            <strong>{t('faq.ownership.question')}</strong> {t('faq.ownership.answer')}
          </li>
          <li>
            <strong>{t('faq.price.question')}</strong> {t('faq.price.answer')}
          </li>
          <li>
            <strong>{t('faq.privacy.question')}</strong> {t('faq.privacy.answer')}
          </li>
          <li>
            <strong>{t('faq.refund.question')}</strong> {t('faq.refund.answer')}
          </li>
          <li>
            <strong>{t('faq.verify.question')}</strong> {t('faq.verify.answer')}
          </li>
          <li>
            <strong>{t('faq.outside_sales.question')}</strong> {t('faq.outside_sales.answer')}
          </li>
        </ul>
      </section>
    </div>
  );
}
